import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Upload, FileText, Brain, Sparkles, Send, ArrowLeft, CheckCircle2, XCircle, AlertTriangle,
  Eye, Copy, RefreshCw, MessageSquare, Mail, ChevronRight, Plus, X, BarChart3,
  TrendingUp, Award, ThumbsUp, ThumbsDown, Scale, DollarSign, Shield, BookOpen, ChevronDown, Check, Clock, Search, Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { proposalAPI, rfpAPI } from '../api';
import { Modal, Avatar, StatusBadge, ProgressBar, Loader, EmptyState } from '../App.core';
import { formatDate, formatRelative } from '../utils/helpers';

const renderMd = (t) => {
  if (!t) return '';
  return t.replace(/^### (.*$)/gim, '<h4 style="font-weight:600;margin:12px 0 6px;font-size:0.9rem;">$1</h4>')
    .replace(/^## (.*$)/gim, '<h3 style="font-weight:650;margin:16px 0 8px;font-size:0.98rem;">$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^- (.*$)/gim, '<div style="padding-left:14px;margin:2px 0;"><span style="color:var(--px-primary);margin-right:5px;">•</span>$1</div>')
    .replace(/\|(.+)\|/g, (m) => { const c = m.split('|').filter(x => x.trim()); if (c.every(x => x.trim().match(/^[-:]+$/))) return ''; return `<div style="display:flex;gap:6px;padding:3px 0;border-bottom:1px solid var(--px-border);font-size:0.8rem;">${c.map(x => `<span style="flex:1;">${x.trim()}</span>`).join('')}</div>`; })
    .replace(/\n\n/g, '<br/><br/>').replace(/\n/g, '<br/>');
};

const VERDICT_CONFIG = {
  exceeds: { label: 'Exceeds', color: '#059669', bg: '#d1fae5', icon: Award },
  compliant: { label: 'Compliant', color: '#059669', bg: '#d1fae5', icon: CheckCircle2 },
  partially_compliant: { label: 'Partial', color: '#d97706', bg: '#fef3c7', icon: AlertTriangle },
  non_compliant: { label: 'Non-Compliant', color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  not_addressed: { label: 'Not Addressed', color: '#6b7280', bg: '#f1f5f9', icon: X },
};

// =============================================
// PROPOSAL LIST & UPLOAD PAGE
// =============================================
export const ProposalListPage = () => {
  const { rfpId } = useParams();
  const navigate = useNavigate();
  const [rfp, setRfp] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [evaluatingId, setEvaluatingId] = useState(null);
  const [evaluationSuccessModal, setEvaluationSuccessModal] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '' });

  const load = useCallback(async () => {
    try {
      const [rfpRes, propRes] = await Promise.all([
        rfpAPI.getById(rfpId),
        proposalAPI.list({ rfp_id: rfpId }),
      ]);
      setRfp(rfpRes.data.rfp);
      setProposals(propRes.data.proposals);
    } catch { toast.error('Failed to load.'); }
    finally { setLoading(false); }
  }, [rfpId]);

  useEffect(() => { load(); }, [load]);

  const handleCompare = async () => {
    setComparing(true);
    try {
      const { data } = await proposalAPI.compare({ rfp_id: rfpId });
      setComparison(data.comparison);
      setShowCompare(true);
    } catch (err) { toast.error(err.response?.data?.error || 'Comparison failed.'); }
    finally { setComparing(false); }
  };

  const handleEvaluate = async (p, e) => {
    e.stopPropagation();
    setEvaluatingId(p.id);
    toast.loading('AI evaluating proposal...', { id: 'eval' });
    try {
      const { data } = await proposalAPI.evaluate(p.id);
      toast.dismiss('eval');
      await load();
      setEvaluationSuccessModal({
        proposalId: p.id,
        vendorName: p.vendor_name,
        overallScore: data?.proposal?.overall_score,
        technicalScore: data?.proposal?.overall_technical_score,
        commercialScore: data?.proposal?.overall_commercial_score
      });
    } catch {
      toast.dismiss('eval');
      toast.error('AI evaluation failed.', { id: 'eval' });
    } finally {
      setEvaluatingId(null);
    }
  };

  const evaluated = proposals.filter(p => p.overall_score !== null);
  const received = proposals.filter(p => p.status === 'received');
  const accepted = proposals.filter(p => p.status === 'accepted');

  const filteredProposals = proposals.filter(p => {
    const matchesSearch = !filters.search ||
      p.vendor_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      p.vendor_company?.toLowerCase().includes(filters.search.toLowerCase()) ||
      p.contact_person?.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = !filters.status || p.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  if (loading) return <Loader />;

  return (
    <div className="page-fade-in">
      {/* Welcome Banner matching Pages.js / Projects design */}
      <div className="dashboard-welcome-banner">
        <div>
          <Link to="/rfps" className="rfp-detail-breadcrumb" style={{ marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--px-text-secondary)', textDecoration: 'none' }}>
            <ArrowLeft size={14} /> Back to RFP Management
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 className="dashboard-welcome-title" style={{ margin: 0 }}>
              Vendor Proposals — {rfp?.title}
            </h2>
            {rfp?.rfp_number && (
              <span className="rfp-detail-number-badge">{rfp?.rfp_number}</span>
            )}
            <span
              className="badge"
              style={{
                background: '#eff6ff',
                color: '#0038A7',
                border: '1px solid #bfdbfe',
                fontWeight: 650
              }}
            >
              {proposals.length} SUBMISSION{proposals.length === 1 ? '' : 'S'}
            </span>
          </div>
          <div className="dashboard-welcome-subtitle" style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <BookOpen size={14} style={{ color: '#0038A7' }} />
              {rfp?.project?.name || 'Project Tender'}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Award size={14} style={{ color: '#059669' }} />
              {evaluated.length} Evaluated with Propel AI
            </span>
          </div>
        </div>

        <div className="dashboard-welcome-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowUpload(true)}
          >
            <Upload size={15} /> Upload Vendor Proposal
          </button>
        </div>
      </div>

      {/* Modern KPI Cards Grid matching Pages.js */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 24 }}>
        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Submissions</span>
            <div className="kpi-card-icon-pill">
              <FileText size={16} />
            </div>
          </div>
          <div className="kpi-value">{proposals.length}</div>
          <div className="kpi-card-footer">
            <span>All received vendor tender proposals</span>
          </div>
        </div>

        <div className="kpi-card kpi-cyan">
          <div className="kpi-card-header">
            <span className="kpi-label">AI Evaluated</span>
            <div className="kpi-card-icon-pill">
              <Brain size={16} />
            </div>
          </div>
          <div className="kpi-value">{evaluated.length}</div>
          <div className="kpi-card-footer">
            <span>Scored across 22 technical & commercial criteria</span>
          </div>
        </div>

        <div className="kpi-card kpi-warning">
          <div className="kpi-card-header">
            <span className="kpi-label">Pending Review</span>
            <div className="kpi-card-icon-pill">
              <Clock size={16} />
            </div>
          </div>
          <div className="kpi-value">{received.length}</div>
          <div className="kpi-card-footer">
            <span>Awaiting automated AI assessment</span>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Accepted Bids</span>
            <div className="kpi-card-icon-pill">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div className="kpi-value">{accepted.length}</div>
          <div className="kpi-card-footer">
            <span>Awarded / accepted commercial tender</span>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar matching Pages.js */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={16} className="search-input-icon" />
          <input
            className="search-input"
            placeholder="Search proposals by vendor name, company, or contact..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <option value="">All Statuses</option>
              <option value="received">Received</option>
              <option value="evaluated">Evaluated</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
        </div>
      </div>

      {/* Proposals Table Card matching Pages.js */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {filteredProposals.length === 0 ? (
          <div style={{ padding: '30px 20px' }}>
            <EmptyState
              icon={FileText}
              title={proposals.length === 0 ? "No proposals received" : "No matching proposals"}
              message={proposals.length === 0 ? "Upload vendor technical and commercial submissions to begin automated scoring & FIDIC compliance checks." : "Try adjusting your search filters to find proposals."}
              action={proposals.length === 0 && (
                <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
                  <Upload size={15} /> Upload First Proposal
                </button>
              )}
            />
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor & Company</th>
                  <th>Status</th>
                  <th>Total Quotation</th>
                  <th>AI Evaluation Score</th>
                  <th>Submitted Files</th>
                  <th>Submission Date</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProposals.map(p => {
                  return (
                    <tr key={p.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div
                            style={{
                              width: 38,
                              height: 38,
                              borderRadius: 8,
                              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
                              color: '#0038A7',
                              border: '1px solid rgba(0, 56, 167, 0.12)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.95rem',
                              flexShrink: 0
                            }}
                          >
                            {p.vendor_name ? p.vendor_name.charAt(0).toUpperCase() : 'V'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 650, color: '#0B1220', fontSize: '0.9rem' }}>
                              {p.vendor_name}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: 'var(--px-text-muted)', marginTop: 2 }}>
                              {p.vendor_company || p.contact_person || 'Direct Contractor'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <span
                          className="badge"
                          style={{
                            background:
                              p.status === 'accepted'
                                ? '#d1fae5'
                                : p.status === 'evaluated'
                                  ? '#dbeafe'
                                  : p.status === 'rejected'
                                    ? '#fee2e2'
                                    : '#f1f5f9',
                            color:
                              p.status === 'accepted'
                                ? '#065f46'
                                : p.status === 'evaluated'
                                  ? '#1d4ed8'
                                  : p.status === 'rejected'
                                    ? '#991b1b'
                                    : '#475569',
                            fontWeight: 650,
                            textTransform: 'uppercase',
                            fontSize: '0.7rem'
                          }}
                        >
                          {p.status}
                        </span>
                      </td>

                      <td>
                        {p.total_price ? (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 650, color: '#0B1220' }}>
                            <DollarSign size={13} style={{ color: '#059669' }} />
                            <span>{Number(p.total_price).toLocaleString()}</span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--px-text-secondary)', fontWeight: 500 }}>{p.currency}</span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--px-text-muted)' }}>—</span>
                        )}
                      </td>

                      <td>
                        {p.overall_score !== null ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 140 }}>
                            <div
                              style={{
                                fontWeight: 750,
                                fontSize: '0.92rem',
                                color: p.overall_score >= 70 ? '#059669' : p.overall_score >= 50 ? '#d97706' : '#dc2626',
                                minWidth: 38
                              }}
                            >
                              {p.overall_score}%
                            </div>
                            <div style={{ flex: 1, maxWidth: 90 }}>
                              <ProgressBar value={p.overall_score} />
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--px-text-muted)', fontSize: '0.8rem', fontStyle: 'italic' }}>
                            Not evaluated
                          </span>
                        )}
                      </td>

                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 220 }}>
                          {p.technical_file_name && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: 'var(--px-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <BookOpen size={12} style={{ color: '#0038A7', flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.technical_file_name}</span>
                            </div>
                          )}
                          {p.commercial_file_name && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: 'var(--px-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <DollarSign size={12} style={{ color: '#d97706', flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.commercial_file_name}</span>
                            </div>
                          )}
                          {!p.technical_file_name && !p.commercial_file_name && (
                            <span style={{ color: 'var(--px-text-muted)', fontSize: '0.76rem' }}>—</span>
                          )}
                        </div>
                      </td>

                      <td>
                        <span style={{ fontSize: '0.82rem', color: 'var(--px-text-secondary)', whiteSpace: 'nowrap' }}>
                          {formatRelative(p.created_at)}
                        </span>
                      </td>

                      <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {(p.status === 'received' || p.overall_score === null) ? (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={(e) => handleEvaluate(p, e)}
                              disabled={evaluatingId === p.id}
                              style={{ fontWeight: 600, minWidth: 108, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                            >
                              {evaluatingId === p.id ? (
                                <>
                                  <Loader2 size={14} className="btn-spinner" />
                                  <span>Evaluating...</span>
                                </>
                              ) : (
                                <>
                                  <Brain size={14} />
                                  <span>Evaluate</span>
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => navigate(`/proposals/${p.id}`)}
                            >
                              <Eye size={14} /> Review
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Evaluation Success Modal */}
      {evaluationSuccessModal && (
        <Modal
          isOpen
          onClose={() => setEvaluationSuccessModal(null)}
          showClose={false}
          size="modal-sm"
          footer={
            <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setEvaluationSuccessModal(null)}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const id = evaluationSuccessModal.proposalId;
                  setEvaluationSuccessModal(null);
                  navigate(`/proposals/${id}`);
                }}
              >
                <Eye size={14} /> Review
              </button>
            </div>
          }
        >
          <div style={{ textAlign: 'center', padding: '12px 6px' }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: '#d1fae5',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px'
              }}
            >
              <CheckCircle2 size={30} />
            </div>
            <h3 style={{ margin: '0 0 6px', fontSize: '1.15rem', fontWeight: 700, color: '#0B1220' }}>
              Evaluation Completed!
            </h3>
            <p style={{ margin: 0, fontSize: '0.86rem', color: 'var(--px-text-secondary)', lineHeight: 1.5 }}>
              Propel AI has completed evaluating <strong>{evaluationSuccessModal.vendorName}</strong>.
            </p>
            {evaluationSuccessModal.overallScore !== undefined && evaluationSuccessModal.overallScore !== null && (
              <div style={{ marginTop: 16, padding: '12px 18px', background: '#f8fafc', borderRadius: 10, border: '1px solid var(--px-border)', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--px-text-muted)', fontWeight: 600 }}>OVERALL SCORE</span>
                <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#059669' }}>{evaluationSuccessModal.overallScore}%</span>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Comparison Modal */}
      {showCompare && comparison && (
        <Modal isOpen onClose={() => setShowCompare(false)} title="AI Vendor Comparison Analysis" size="modal-lg">
          <div style={{ maxHeight: '60vh', overflowY: 'auto', fontSize: '0.88rem', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: renderMd(comparison) }} />
          <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { navigator.clipboard.writeText(comparison); toast.success('Copied'); }}><Copy size={14} /> Copy</button>
          </div>
        </Modal>
      )}

      {showUpload && <UploadProposalModal rfpId={rfpId} onClose={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); load(); }} />}
    </div>
  );
};

// Add this to client/src/pages/ProposalEval.js

export const GlobalProposalListPage = () => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [rfps, setRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState('');
  const [evaluatingId, setEvaluatingId] = useState(null);
  const [evaluationSuccessModal, setEvaluationSuccessModal] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: '', rfp_id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [propRes, rfpRes] = await Promise.all([
        proposalAPI.list(), // Modified backend to fetch all if no ID passed
        rfpAPI.list({ limit: 100 })
      ]);
      setProposals(propRes.data.proposals);
      setRfps(rfpRes.data.rfps);
    } catch {
      toast.error('Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEvaluate = async (p, e) => {
    if (e) e.stopPropagation();
    setEvaluatingId(p.id);
    toast.loading('AI evaluating section-by-section...', { id: 'eval' });
    try {
      const { data } = await proposalAPI.evaluate(p.id);
      toast.dismiss('eval');
      await load();
      setEvaluationSuccessModal({
        proposalId: p.id,
        vendorName: p.vendor_name,
        overallScore: data?.proposal?.overall_score
      });
    } catch {
      toast.dismiss('eval');
      toast.error('AI evaluation failed.', { id: 'eval' });
    } finally {
      setEvaluatingId(null);
    }
  };

  if (loading) return <Loader />;

  const totalProposals = proposals.length;
  const evaluatedCount = proposals.filter(p => p.overall_score !== null).length;
  const receivedCount = proposals.filter(p => p.status === 'received').length;
  const acceptedCount = proposals.filter(p => p.status === 'accepted').length;

  const filteredProposals = proposals.filter(p => {
    if (filters.status && p.status !== filters.status) return false;
    if (filters.rfp_id && p.rfp_id !== filters.rfp_id) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const matchVendor = p.vendor_name?.toLowerCase().includes(q) || p.vendor_company?.toLowerCase().includes(q);
      const matchRfp = p.rfp?.title?.toLowerCase().includes(q) || p.rfp?.rfp_number?.toLowerCase().includes(q) || p.rfp?.project?.name?.toLowerCase().includes(q);
      if (!matchVendor && !matchRfp) return false;
    }
    return true;
  });

  return (
    <>
      {/* Top Banner matching RFPBuilder and Dashboard */}
      <div className="dashboard-welcome-banner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 className="dashboard-welcome-title">Vendor Proposals</h2>
          <div className="dashboard-welcome-subtitle">
            <span>AI-assisted evaluation, technical scoring, and tender submissions across all procurement packages</span>
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowUpload(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} />
          <span>New Proposal</span>
        </button>
      </div>

      {/* KPI Cards Grid matching RFPBuilder */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-info">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Submissions</span>
            <div className="kpi-card-icon-pill">
              <FileText size={16} />
            </div>
          </div>
          <div className="kpi-value">{totalProposals}</div>
          <div className="kpi-card-footer">
            <span>Across all active packages</span>
          </div>
        </div>

        <div className="kpi-card kpi-primary">
          <div className="kpi-card-header">
            <span className="kpi-label">AI Evaluated</span>
            <div className="kpi-card-icon-pill">
              <Brain size={16} />
            </div>
          </div>
          <div className="kpi-value">{evaluatedCount}</div>
          <div className="kpi-card-footer">
            <span>Compliance & technical scored</span>
          </div>
        </div>

        <div className="kpi-card kpi-muted">
          <div className="kpi-card-header">
            <span className="kpi-label">Pending Evaluation</span>
            <div className="kpi-card-icon-pill">
              <Clock size={16} />
            </div>
          </div>
          <div className="kpi-value">{receivedCount}</div>
          <div className="kpi-card-footer">
            <span>Awaiting AI evaluation</span>
          </div>
        </div>

        <div className="kpi-card kpi-success">
          <div className="kpi-card-header">
            <span className="kpi-label">Accepted Tenders</span>
            <div className="kpi-card-icon-pill">
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div className="kpi-value">{acceptedCount}</div>
          <div className="kpi-card-footer">
            <span>Awarded contracts</span>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar matching RFPBuilder */}
      <div className="filter-bar">
        <div className="search-input-wrapper">
          <Search size={16} className="search-input-icon" />
          <input
            className="search-input"
            placeholder="Search proposals by vendor, RFP title, or code..."
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <option value="">All Statuses</option>
              <option value="received">Received</option>
              <option value="evaluated">Evaluated</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>

          <div className="filter-select-wrapper">
            <select
              className="filter-select"
              value={filters.rfp_id}
              onChange={e => setFilters(f => ({ ...f, rfp_id: e.target.value }))}
            >
              <option value="">All RFP Packages</option>
              {rfps.map(r => (
                <option key={r.id} value={r.id}>
                  {r.rfp_number}: {r.title}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="filter-select-chevron" />
          </div>
        </div>
      </div>

      {/* Proposals Table Card matching RFPBuilder */}
      <div className="card">
        {filteredProposals.length === 0 ? (
          <div style={{ padding: '20px 0' }}>
            <EmptyState
              icon={FileText}
              title="No proposals found"
              message="Proposals appear here once uploaded or received via RFP links."
            />
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>RFP Context</th>
                  <th>Status</th>
                  <th>Scores</th>
                  <th>Price</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProposals.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#0B1220' }}>{p.vendor_name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--px-text-muted)', marginTop: 2 }}>{p.vendor_company || 'Direct Contractor'}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0B1220' }}>{p.rfp?.title}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--px-text-muted)', marginTop: 2 }}>{p.rfp?.rfp_number} • {p.rfp?.project?.project_code || p.rfp?.project?.name}</div>
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          background:
                            p.status === 'accepted'
                              ? '#d1fae5'
                              : p.status === 'evaluated'
                                ? '#dbeafe'
                                : p.status === 'rejected'
                                  ? '#fee2e2'
                                  : '#f1f5f9',
                          color:
                            p.status === 'accepted'
                              ? '#065f46'
                              : p.status === 'evaluated'
                                ? '#1d4ed8'
                                : p.status === 'rejected'
                                  ? '#991b1b'
                                  : '#475569',
                          fontWeight: 650,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem'
                        }}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td>
                      {p.overall_score !== null ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ fontWeight: 700, color: p.overall_score >= 70 ? 'var(--px-success)' : '#d97706', minWidth: 34 }}>{p.overall_score}%</div>
                          <div style={{ width: 50 }}><ProgressBar value={p.overall_score} /></div>
                        </div>
                      ) : <span style={{ color: 'var(--px-text-muted)', fontSize: '0.8rem' }}>Not evaluated</span>}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0B1220' }}>
                        {p.total_price ? `${Number(p.total_price).toLocaleString()} ${p.currency || 'AED'}` : '—'}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {(p.status === 'received' || p.overall_score === null) ? (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={(e) => handleEvaluate(p, e)}
                            disabled={evaluatingId === p.id}
                            style={{ fontWeight: 600, minWidth: 108, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                          >
                            {evaluatingId === p.id ? (
                              <>
                                <Loader2 size={14} className="btn-spinner" />
                                <span>Evaluating...</span>
                              </>
                            ) : (
                              <>
                                <Brain size={14} />
                                <span>Evaluate</span>
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => navigate(`/proposals/${p.id}`)}
                          >
                            <Eye size={14} /> Review
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Global Evaluation Success Modal */}
      {evaluationSuccessModal && (
        <Modal
          isOpen
          onClose={() => setEvaluationSuccessModal(null)}
          showClose={false}
          size="modal-sm"
          footer={
            <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setEvaluationSuccessModal(null)}>
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const id = evaluationSuccessModal.proposalId;
                  setEvaluationSuccessModal(null);
                  navigate(`/proposals/${id}`);
                }}
              >
                <Eye size={14} /> Review
              </button>
            </div>
          }
        >
          <div style={{ textAlign: 'center', padding: '12px 6px' }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: '#d1fae5',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px'
              }}
            >
              <CheckCircle2 size={30} />
            </div>
            <h3 style={{ margin: '0 0 6px', fontSize: '1.15rem', fontWeight: 700, color: '#0B1220' }}>
              Evaluation Completed!
            </h3>
            <p style={{ margin: 0, fontSize: '0.86rem', color: 'var(--px-text-secondary)', lineHeight: 1.5 }}>
              Propel AI has completed evaluating <strong>{evaluationSuccessModal.vendorName}</strong>.
            </p>
            {evaluationSuccessModal.overallScore !== undefined && evaluationSuccessModal.overallScore !== null && (
              <div style={{ marginTop: 16, padding: '12px 18px', background: '#f8fafc', borderRadius: 10, border: '1px solid var(--px-border)', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--px-text-muted)', fontWeight: 600 }}>OVERALL SCORE</span>
                <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#059669' }}>{evaluationSuccessModal.overallScore}%</span>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Global Upload Modal */}
      {showUpload && (
        selectedRfpId ? (
          <UploadProposalModal
            rfpId={selectedRfpId}
            onClose={() => { setShowUpload(false); setSelectedRfpId(''); }}
            onSuccess={() => { setShowUpload(false); setSelectedRfpId(''); load(); }}
          />
        ) : (
          <Modal
            isOpen
            onClose={() => setShowUpload(false)}
            showClose={false}
            size="modal-md"
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
                    color: '#0038A7',
                    border: '1px solid rgba(0, 56, 167, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Upload size={22} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
                    Select Target RFP
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
                    Choose the active procurement package for this proposal
                  </p>
                </div>
              </div>
            }
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
                <button className="btn btn-secondary" onClick={() => setShowUpload(false)}>
                  Cancel
                </button>
              </div>
            }
          >
            <div className="form-group" style={{ margin: '8px 0' }}>
              <label className="form-label">Active Tender Package <span className="required">*</span></label>
              <div className="form-select-wrapper">
                <select className="form-select" value={selectedRfpId} onChange={e => setSelectedRfpId(e.target.value)}>
                  <option value="">-- Choose an active RFP package --</option>
                  {rfps.map(r => <option key={r.id} value={r.id}>{r.rfp_number}: {r.title} ({r.project?.name})</option>)}
                </select>
                <ChevronDown size={14} className="form-select-chevron" />
              </div>
            </div>
          </Modal>
        )
      )}
    </>
  );
};

// =============================================
// UPLOAD PROPOSAL MODAL
// =============================================
const UploadProposalModal = ({ rfpId, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    vendor_name: '',
    vendor_company: '',
    vendor_email: '',
    contact_person: '',
    total_price: '',
    currency: 'AED',
    validity_period: '90 days'
  });
  const [techFile, setTechFile] = useState(null);
  const [commFile, setCommFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async () => {
    const errors = {};
    if (!form.vendor_name.trim()) errors.vendor_name = 'Vendor name is required';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error('Please fill in required fields');
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('rfp_id', rfpId);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (techFile) fd.append('technical_file', techFile);
      if (commFile) fd.append('commercial_file', commFile);
      await proposalAPI.create(fd);
      toast.success('Proposal uploaded successfully!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setSaving(false);
    }
  };

  const set = (f) => (e) => {
    if (fieldErrors[f]) {
      setFieldErrors(prev => {
        const copy = { ...prev };
        delete copy[f];
        return copy;
      });
    }
    setForm(prev => ({ ...prev, [f]: e.target.value }));
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-lg"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Upload size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              Upload Vendor Proposal
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              Submit technical and commercial tender documentation for AI evaluation and scoring
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving}
            style={{ minWidth: 140 }}
          >
            {saving ? 'Uploading...' : 'Upload Proposal'}
          </button>
        </div>
      }
    >
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Vendor Name <span className="required">*</span></label>
          <input
            className={`form-input ${fieldErrors.vendor_name ? 'field-error' : ''}`}
            value={form.vendor_name}
            onChange={set('vendor_name')}
            placeholder="e.g. Al Habtoor MEP Engineering LLC"
          />
          {fieldErrors.vendor_name && (
            <div style={{ color: '#DC2626', fontSize: '0.78rem', marginTop: 4, fontWeight: 500 }}>
              {fieldErrors.vendor_name}
            </div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Company / Trade Division</label>
          <input
            className="form-input"
            value={form.vendor_company}
            onChange={set('vendor_company')}
            placeholder="e.g. Mechanical & Electrical Division"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Vendor Email</label>
          <input
            className="form-input"
            type="email"
            value={form.vendor_email}
            onChange={set('vendor_email')}
            placeholder="e.g. tenders@alhabtoor.ae"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Contact Person</label>
          <input
            className="form-input"
            value={form.contact_person}
            onChange={set('contact_person')}
            placeholder="e.g. Eng. Tariq Mansoor (Bid Manager)"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Total Quotation Price</label>
          <input
            className="form-input"
            type="number"
            value={form.total_price}
            onChange={set('total_price')}
            placeholder="e.g. 18500000"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Currency</label>
          <div className="form-select-wrapper">
            <select className="form-select" value={form.currency} onChange={set('currency')}>
              <option value="AED">AED — UAE Dirham</option>
              <option value="SAR">SAR — Saudi Riyal</option>
              <option value="USD">USD — US Dollar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — British Pound</option>
              <option value="INR">INR — Indian Rupee</option>
            </select>
            <ChevronDown size={14} className="form-select-chevron" />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Tender Validity Period</label>
          <div className="form-select-wrapper">
            <select className="form-select" value={form.validity_period} onChange={set('validity_period')}>
              <option value="60 days">60 Days</option>
              <option value="90 days">90 Days (Recommended)</option>
              <option value="120 days">120 Days</option>
              <option value="180 days">180 Days</option>
            </select>
            <ChevronDown size={14} className="form-select-chevron" />
          </div>
        </div>
        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <span className="form-label" style={{ visibility: 'hidden' }}>Hint</span>
          <div style={{ fontSize: '0.8rem', color: 'var(--px-text-muted)', display: 'flex', alignItems: 'center', gap: 6, paddingTop: 6 }}>
            <Clock size={14} style={{ color: '#0038A7' }} /> Standard validity ensures price protection during AI review
          </div>
        </div>
      </div>

      {/* Tender Document Upload Packages */}
      <div style={{ marginTop: 6, marginBottom: 4 }}>
        <label className="form-label" style={{ marginBottom: 8, fontSize: '0.84rem', fontWeight: 600 }}>
          Proposal Submission Packages
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {/* Technical Package */}
          <div
            onClick={() => document.getElementById('techFileInput').click()}
            style={{
              padding: '18px 16px',
              border: techFile ? '1.5px solid #0038A7' : '1.5px dashed #CBD5E1',
              borderRadius: 10,
              background: techFile ? '#eff6ff' : '#FAFAFA',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative'
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                background: techFile ? 'rgba(0, 56, 167, 0.12)' : 'rgba(0, 56, 167, 0.08)',
                color: '#0038A7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 10
              }}
            >
              <BookOpen size={20} />
            </div>
            <div style={{ fontWeight: 650, fontSize: '0.88rem', color: '#0B1220' }}>
              Technical Proposal
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--px-text-muted)', marginTop: 4, lineHeight: 1.4 }}>
              {techFile ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4 }}>
                  <span style={{ fontWeight: 600, color: '#0038A7', wordBreak: 'break-all' }}>{techFile.name}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setTechFile(null); }}
                    style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', padding: 2 }}
                    title="Remove file"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                'Upload PDF, DOCX or technical specs (Methodology, QA/QC)'
              )}
            </div>
            <input
              id="techFileInput"
              type="file"
              accept=".pdf,.docx,.doc"
              style={{ display: 'none' }}
              onChange={e => setTechFile(e.target.files[0])}
            />
          </div>

          {/* Commercial Package */}
          <div
            onClick={() => document.getElementById('commFileInput').click()}
            style={{
              padding: '18px 16px',
              border: commFile ? '1.5px solid #d97706' : '1.5px dashed #CBD5E1',
              borderRadius: 10,
              background: commFile ? '#fffbeb' : '#FAFAFA',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative'
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                background: commFile ? 'rgba(217, 119, 6, 0.15)' : 'rgba(217, 119, 6, 0.08)',
                color: '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 10
              }}
            >
              <DollarSign size={20} />
            </div>
            <div style={{ fontWeight: 650, fontSize: '0.88rem', color: '#0B1220' }}>
              Commercial & BOQ
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--px-text-muted)', marginTop: 4, lineHeight: 1.4 }}>
              {commFile ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4 }}>
                  <span style={{ fontWeight: 600, color: '#d97706', wordBreak: 'break-all' }}>{commFile.name}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCommFile(null); }}
                    style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', padding: 2 }}
                    title="Remove file"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                'Upload priced BOQ, schedule of rates or cost summaries'
              )}
            </div>
            <input
              id="commFileInput"
              type="file"
              accept=".pdf,.docx,.doc,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={e => setCommFile(e.target.files[0])}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

// =============================================
// SIDE-BY-SIDE EVALUATION REVIEW PAGE
// =============================================
export const ProposalReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEval, setActiveEval] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await proposalAPI.getById(id);
      setProposal(data.proposal);
      if (!activeEval && data.proposal.evaluations?.length > 0) setActiveEval(data.proposal.evaluations[0]);
    } catch { toast.error('Failed to load.'); navigate('/rfps'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleEvaluate = async () => {
    setEvaluating(true);
    try {
      await proposalAPI.evaluate(id);
      toast.success('AI Evaluation complete!');
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Evaluation failed.'); }
    finally { setEvaluating(false); }
  };

  const handleSaveComment = async () => {
    if (!commentText.trim() || !activeEval) return;
    try {
      await proposalAPI.addComment(proposal.id, activeEval.id, { comment: commentText });
      toast.success('Comment saved.');
      setCommentText('');
      await load();
    } catch { toast.error('Failed.'); }
  };

  const handleStatusChange = async (status) => {
    try {
      await proposalAPI.updateStatus(proposal.id, { status });
      toast.success(`Proposal ${status}.`);
      await load();
    } catch { toast.error('Failed.'); }
  };

  const [sectionFilter, setSectionFilter] = useState('');
  const [activeTab, setActiveTab] = useState('evaluation'); // 'evaluation' | 'summary'

  if (loading) return <Loader />;
  if (!proposal) return null;

  const evals = (proposal.evaluations || []).sort((a, b) => (a.rfp_section?.sort_order || 0) - (b.rfp_section?.sort_order || 0));
  const techEvals = evals.filter(e => e.section_category === 'technical');
  const commEvals = evals.filter(e => e.section_category === 'commercial');

  const filteredTechEvals = techEvals.filter(e => 
    !sectionFilter || (e.section_title || '').toLowerCase().includes(sectionFilter.toLowerCase())
  );
  const filteredCommEvals = commEvals.filter(e => 
    !sectionFilter || (e.section_title || '').toLowerCase().includes(sectionFilter.toLowerCase())
  );

  const backUrl = proposal.rfp_id ? `/rfps/${proposal.rfp_id}/proposals` : '/proposals';

  return (
    <div className="rfp-detail-container">
      {/* Top Header Card */}
      <div className="rfp-detail-top-card">
        <div className="rfp-detail-header-main">
          <div>
            <Link to={backUrl} className="rfp-detail-breadcrumb">
              <ArrowLeft size={14} /> Back to Proposals
            </Link>
            <div className="rfp-detail-title-wrap">
              <h1 className="rfp-detail-title">
                {proposal.vendor_name}
              </h1>
              {proposal.vendor_company && (
                <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--px-text-secondary)' }}>
                  ({proposal.vendor_company})
                </span>
              )}
              {proposal.rfp?.rfp_number && (
                <span className="rfp-detail-number-badge">
                  {proposal.rfp.rfp_number}
                </span>
              )}
              <span
                className="badge"
                style={{
                  background:
                    proposal.status === 'accepted' ? '#d1fae5' :
                    proposal.status === 'rejected' ? '#fee2e2' :
                    proposal.status === 'evaluated' ? '#eff6ff' : '#f1f5f9',
                  color:
                    proposal.status === 'accepted' ? '#059669' :
                    proposal.status === 'rejected' ? '#dc2626' :
                    proposal.status === 'evaluated' ? '#0038A7' : '#475569',
                  fontWeight: 650,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                {proposal.status || 'Draft'}
              </span>
            </div>
            <div className="rfp-detail-submeta">
              {proposal.rfp?.title && (
                <span className="rfp-detail-submeta-item">
                  <FileText size={14} style={{ color: '#0038A7' }} />
                  <strong>{proposal.rfp.title}</strong>
                </span>
              )}
              {proposal.total_price && (
                <span className="rfp-detail-submeta-item">
                  <DollarSign size={14} style={{ color: '#d97706' }} />
                  Quotation: <strong>{Number(proposal.total_price).toLocaleString()} {proposal.currency || 'AED'}</strong>
                </span>
              )}
              {proposal.created_at && (
                <span className="rfp-detail-submeta-item">
                  <Clock size={14} style={{ color: '#64748b' }} />
                  Submitted: {formatDate(proposal.created_at)}
                </span>
              )}
            </div>
          </div>

          <div className="rfp-detail-actions">
            {evals.length === 0 && (
              <button className="btn btn-primary" onClick={handleEvaluate} disabled={evaluating}>
                <Brain size={15} /> {evaluating ? 'Evaluating...' : 'AI Evaluate'}
              </button>
            )}
            <button
              className="btn btn-secondary"
              style={{ color: '#059669', borderColor: '#a7f3d0' }}
              onClick={() => handleStatusChange('accepted')}
            >
              <ThumbsUp size={14} /> Accept Proposal
            </button>
            <button
              className="btn btn-secondary"
              style={{ color: '#dc2626', borderColor: '#fecaca' }}
              onClick={() => handleStatusChange('rejected')}
            >
              <ThumbsDown size={14} /> Reject
            </button>
            <button className="btn btn-secondary" onClick={() => setShowFeedback(true)}>
              <Mail size={14} /> Send Feedback
            </button>
          </div>
        </div>
      </div>

      {/* Modern KPI Cards Grid */}
      {proposal.overall_score !== null && (
        <div className="kpi-grid proposal-review-kpi-grid" style={{ marginBottom: 0, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          {/* Technical Score */}
          <div className="kpi-card kpi-primary" style={{ padding: '12px 16px' }}>
            <div className="kpi-card-header" style={{ marginBottom: 6 }}>
              <span className="kpi-label">Technical Score</span>
              <div className="kpi-card-icon-pill">
                <BookOpen size={16} />
              </div>
            </div>
            <div className="kpi-value" style={{ fontSize: '1.6rem', marginBottom: 6 }}>
              {proposal.overall_technical_score ?? '—'}%
            </div>
            <ProgressBar value={proposal.overall_technical_score || 0} />
          </div>

          {/* Commercial Score */}
          <div className="kpi-card kpi-warning" style={{ padding: '12px 16px' }}>
            <div className="kpi-card-header" style={{ marginBottom: 6 }}>
              <span className="kpi-label">Commercial Score</span>
              <div className="kpi-card-icon-pill">
                <DollarSign size={16} />
              </div>
            </div>
            <div className="kpi-value" style={{ fontSize: '1.6rem', marginBottom: 6 }}>
              {proposal.overall_commercial_score ?? '—'}%
            </div>
            <ProgressBar value={proposal.overall_commercial_score || 0} />
          </div>

          {/* Overall Score */}
          <div className={`kpi-card ${proposal.overall_score >= 70 ? 'kpi-success' : 'kpi-warning'}`} style={{ padding: '12px 16px' }}>
            <div className="kpi-card-header" style={{ marginBottom: 6 }}>
              <span className="kpi-label">Overall Score</span>
              <div className="kpi-card-icon-pill">
                <Award size={16} />
              </div>
            </div>
            <div className="kpi-value" style={{ fontSize: '1.6rem', marginBottom: 6 }}>
              {proposal.overall_score ?? '—'}%
            </div>
            <ProgressBar value={proposal.overall_score || 0} />
          </div>

        </div>
      )}

      {proposal.overall_score !== null && (
        <div className="proposal-review-context-row proposal-review-recommendation-row">
          <div className="proposal-review-context-icon kpi-cyan"><Sparkles size={17} /></div>
          <div className="proposal-review-context-content">
            <span className="kpi-label">AI Recommendation</span>
            <div className="proposal-review-recommendation">
              {proposal.ai_recommendation || 'Evaluation in progress'}
            </div>
            <div className="kpi-card-footer"><CheckCircle2 size={12} style={{ color: '#00AFC0' }} /> Aligned with RFP Criteria</div>
          </div>
        </div>
      )}

      {proposal.ai_summary && !evaluating && (
        <div className="proposal-review-context-row proposal-review-summary-card">
          <div className="proposal-review-context-icon proposal-review-summary-icon"><Brain size={17} /></div>
          <div className="proposal-review-context-content">
            <div className="proposal-review-summary-heading">
              <span className="kpi-label">AI Executive Summary</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  navigator.clipboard.writeText(proposal.ai_summary);
                  toast.success('Executive summary copied to clipboard');
                }}
              >
                <Copy size={13} /> Copy Summary
              </button>
            </div>
            <div className="proposal-review-summary-text" dangerouslySetInnerHTML={{ __html: renderMd(proposal.ai_summary) }} />
          </div>
        </div>
      )}

      {/* Evaluating State */}
      {evaluating && (
        <div
          className="card"
          style={{
            textAlign: 'center',
            padding: 40,
            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
            border: '1px solid #bfdbfe'
          }}
        >
          <div className="spinner" style={{ margin: '0 auto 16px', borderTopColor: '#0038A7' }} />
          <h3 style={{ fontWeight: 700, fontSize: '1.1rem', color: '#0b1220', marginBottom: 6 }}>
            Propel AI is evaluating this proposal section by section...
          </h3>
          <p style={{ fontSize: '0.86rem', color: 'var(--px-text-secondary)', maxWidth: 540, margin: '0 auto' }}>
            Benchmarking vendor submissions against all technical specifications and commercial parameters. This takes 30-60 seconds.
          </p>
        </div>
      )}

      {/* Side-by-Side Workbench Layout */}
      {evals.length > 0 && !evaluating && (
        <div className="rfp-workspace-grid" style={{ minHeight: 0 }}>
          {/* Navigation Panel */}
          <div className="rfp-nav-panel">
            <div className="rfp-nav-header">
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                <input
                  type="text"
                  placeholder="Filter sections..."
                  className="rfp-nav-search-input"
                  value={sectionFilter}
                  onChange={e => setSectionFilter(e.target.value)}
                />
              </div>
            </div>

            <div className="rfp-nav-list">
              {/* Technical Group */}
              <div className="rfp-nav-category-header tech">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <BookOpen size={13} /> Technical Evaluation
                </span>
                <span style={{ fontSize: '0.72rem', opacity: 0.85 }}>
                  {techEvals.filter(e => e.verdict === 'compliant' || e.verdict === 'exceeds').length}/{techEvals.length} pass
                </span>
              </div>

              {filteredTechEvals.map(ev => {
                const vc = VERDICT_CONFIG[ev.verdict] || VERDICT_CONFIG.not_addressed;
                const isActive = activeEval?.id === ev.id;
                const IconComponent = vc.icon;
                return (
                  <div
                    key={ev.id}
                    className={`rfp-nav-item ${isActive ? 'active-tech' : ''}`}
                    onClick={() => { setActiveEval(ev); setCommentText(''); }}
                  >
                    <IconComponent size={15} style={{ color: vc.color, marginTop: 2, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="rfp-nav-item-title">{ev.section_title || ev.rfp_section?.title || 'Technical compliance review'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
                        <span style={{ fontSize: '0.7rem', color: vc.color, fontWeight: 600 }}>
                          {vc.label}
                        </span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 650, color: 'var(--px-text-secondary)' }}>
                          {ev.score}/{ev.max_score}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Commercial Group */}
              <div className="rfp-nav-category-header comm" style={{ marginTop: 8, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <DollarSign size={13} /> Commercial Evaluation
                </span>
                <span style={{ fontSize: '0.72rem', opacity: 0.85 }}>
                  {commEvals.filter(e => e.verdict === 'compliant' || e.verdict === 'exceeds').length}/{commEvals.length} pass
                </span>
              </div>

              {filteredCommEvals.map(ev => {
                const vc = VERDICT_CONFIG[ev.verdict] || VERDICT_CONFIG.not_addressed;
                const isActive = activeEval?.id === ev.id;
                const IconComponent = vc.icon;
                return (
                  <div
                    key={ev.id}
                    className={`rfp-nav-item ${isActive ? 'active-comm' : ''}`}
                    onClick={() => { setActiveEval(ev); setCommentText(''); }}
                  >
                    <IconComponent size={15} style={{ color: vc.color, marginTop: 2, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="rfp-nav-item-title">{ev.section_title || ev.rfp_section?.title || 'Commercial review'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
                        <span style={{ fontSize: '0.7rem', color: vc.color, fontWeight: 600 }}>
                          {vc.label}
                        </span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 650, color: 'var(--px-text-secondary)' }}>
                          {ev.score}/{ev.max_score}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Evaluation Detail Panel */}
          <div className="rfp-editor-panel" style={{ display: 'flex', flexDirection: 'column' }}>
            {activeEval ? (() => {
              const vc = VERDICT_CONFIG[activeEval.verdict] || VERDICT_CONFIG.not_addressed;
              const IconComponent = vc.icon;
              return (
                <>
                  {/* Top Verdict Banner */}
                  <div
                    style={{
                      padding: '12px 20px',
                      background: vc.bg,
                      borderBottom: `1px solid ${vc.color}35`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: vc.color,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}
                      >
                        <IconComponent size={18} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '1.02rem', color: '#0b1220' }}>
                          {activeEval.section_title || activeEval.rfp_section?.title || 'Proposal compliance review'}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: vc.color, fontWeight: 650 }}>
                          Verdict: {vc.label} &bull; Score: {activeEval.score}/{activeEval.max_score}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        className="badge"
                        style={{
                          background: (activeEval.section_category || activeEval.rfp_section?.section_category) === 'technical' ? '#eff6ff' : '#fffbeb',
                          color: (activeEval.section_category || activeEval.rfp_section?.section_category) === 'technical' ? '#0038A7' : '#92400e',
                          border: `1px solid ${(activeEval.section_category || activeEval.rfp_section?.section_category) === 'technical' ? '#bfdbfe' : '#fde68a'}`,
                          fontWeight: 650,
                          textTransform: 'uppercase',
                          fontSize: '0.72rem'
                        }}
                      >
                        {activeEval.section_category || activeEval.rfp_section?.section_category || 'review'}
                      </span>
                    </div>
                  </div>

                  {/* Scrollable Evaluation Body */}
                  <div className="rfp-editor-body" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '18px 22px' }}>
                    {/* Two-Column Side-by-Side: RFP Requirement vs AI Evaluation */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      {/* Left: RFP Requirement */}
                      <div
                        style={{
                          background: '#f8fafc',
                          borderRadius: 10,
                          border: '1px solid var(--px-border)',
                          padding: 16,
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            color: '#0038A7',
                            marginBottom: 10,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                        >
                          <FileText size={15} /> Prescribed RFP Requirement
                        </div>
                        {activeEval.rfp_section?.content ? (
                          <div
                            style={{
                              fontSize: '0.84rem',
                              lineHeight: 1.65,
                              color: 'var(--px-text-secondary)',
                              maxHeight: 280,
                              overflowY: 'auto'
                            }}
                            dangerouslySetInnerHTML={{ __html: renderMd(activeEval.rfp_section.content.substring(0, 2500)) }}
                          />
                        ) : (
                          <p style={{ fontSize: '0.84rem', color: 'var(--px-text-muted)', margin: 'auto 0' }}>
                            RFP section content not available or not linked.
                          </p>
                        )}
                      </div>

                      {/* Right: AI Evaluation */}
                      <div
                        style={{
                          background: '#ffffff',
                          borderRadius: 10,
                          border: '1px solid #e0e7ff',
                          padding: 16,
                          boxShadow: '0 1px 4px rgba(99, 102, 241, 0.04)',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            color: '#6366f1',
                            marginBottom: 10,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                        >
                          <Sparkles size={15} /> AI Compliance Analysis
                        </div>
                        <div
                          style={{
                            fontSize: '0.84rem',
                            lineHeight: 1.65,
                            color: 'var(--px-text-secondary)',
                            maxHeight: 280,
                            overflowY: 'auto'
                          }}
                          dangerouslySetInnerHTML={{ __html: renderMd(activeEval.ai_analysis || 'No AI analysis available for this section.') }}
                        />
                      </div>
                    </div>

                    {/* Strengths & Gaps Two-Column */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div
                        style={{
                          background: '#f0fdf4',
                          borderRadius: 10,
                          border: '1px solid #bbf7d0',
                          borderLeft: '4px solid #059669',
                          padding: 14
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#059669', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CheckCircle2 size={15} /> Identified Strengths
                        </div>
                        <div
                          style={{ fontSize: '0.83rem', lineHeight: 1.6, color: '#166534' }}
                          dangerouslySetInnerHTML={{ __html: renderMd(activeEval.strengths || 'None identified.') }}
                        />
                      </div>

                      <div
                        style={{
                          background: '#fef2f2',
                          borderRadius: 10,
                          border: '1px solid #fecaca',
                          borderLeft: '4px solid #dc2626',
                          padding: 14
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#dc2626', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <AlertTriangle size={15} /> Deviations & Gaps
                        </div>
                        <div
                          style={{ fontSize: '0.83rem', lineHeight: 1.6, color: '#991b1b' }}
                          dangerouslySetInnerHTML={{ __html: renderMd(activeEval.gaps || 'None identified.') }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Evaluator Comment Bottom Bar */}
                  <div
                    style={{
                      background: '#f8fafc',
                      borderTop: '1px solid var(--px-border)',
                      padding: '12px 20px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      flexShrink: 0
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 650, color: 'var(--px-text)' }}>
                        Evaluator Review Notes & Comments
                      </span>
                      {activeEval.evaluator_comment && (
                        <span style={{ fontSize: '0.72rem', color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Check size={12} /> Notes recorded
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <textarea
                        value={commentText || activeEval.evaluator_comment || ''}
                        onChange={e => setCommentText(e.target.value)}
                        placeholder="Type evaluator observation notes for this section..."
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid var(--px-border)',
                          borderRadius: 8,
                          fontSize: '0.84rem',
                          minHeight: 48,
                          maxHeight: 120,
                          resize: 'vertical',
                          outline: 'none',
                          background: '#ffffff',
                          fontFamily: 'inherit'
                        }}
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSaveComment}
                        style={{
                          alignSelf: 'flex-end',
                          height: 38,
                          padding: '0 16px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6
                        }}
                      >
                        <Send size={13} /> Save Note
                      </button>
                    </div>
                  </div>
                </>
              );
            })() : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 48, color: '#94a3b8' }}>
                <Eye size={48} style={{ opacity: 0.25, marginBottom: 12 }} />
                <p style={{ fontSize: '0.95rem', fontWeight: 600, color: '#475569' }}>Select a section from the index</p>
                <p style={{ fontSize: '0.82rem' }}>Pick any technical or commercial section on the left to review RFP criteria vs AI evaluation.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Executive Summary Card */}
      {proposal.ai_summary && !evaluating && (
        <div
          className="card proposal-review-summary-legacy"
          style={{
            padding: '14px 20px',
            flexShrink: 0,
            background: '#ffffff',
            borderRadius: 12,
            border: '1px solid var(--px-border)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: 'rgba(99, 102, 241, 0.1)',
                  color: '#6366f1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Brain size={16} />
              </div>
              <div>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0b1220' }}>
                  AI Executive Summary
                </span>
                <span style={{ fontSize: '0.74rem', color: 'var(--px-text-muted)', marginLeft: 8 }}>
                  Comprehensive synthesis across all technical and commercial areas
                </span>
              </div>
            </div>
            <button
              className="btn btn-ghost btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              onClick={() => {
                navigator.clipboard.writeText(proposal.ai_summary);
                toast.success('Executive summary copied to clipboard');
              }}
            >
              <Copy size={13} /> Copy Summary
            </button>
          </div>
          <div
            style={{
              fontSize: '0.85rem',
              lineHeight: 1.7,
              color: 'var(--px-text-secondary)',
              maxHeight: 140,
              overflowY: 'auto',
              padding: '6px 2px'
            }}
            dangerouslySetInnerHTML={{ __html: renderMd(proposal.ai_summary) }}
          />
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedback && <FeedbackModal proposal={proposal} onClose={() => setShowFeedback(false)} onSuccess={load} />}
    </div>
  );
};

// =============================================
// FEEDBACK MODAL
// =============================================
const FeedbackModal = ({ proposal, onClose, onSuccess }) => {
  const [content, setContent] = useState(proposal.feedback_content || '');
  const [sending, setSending] = useState(false);
  const [fieldError, setFieldError] = useState('');

  const handleSend = async () => {
    if (!content.trim()) {
      setFieldError('Feedback message content is required.');
      return;
    }
    setSending(true);
    setFieldError('');
    try {
      await proposalAPI.sendFeedback(proposal.id, { feedback_content: content });
      toast.success('Feedback sent to vendor successfully!');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send feedback.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      showClose={false}
      size="modal-lg"
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, rgba(0, 56, 167, 0.08) 0%, rgba(0, 175, 192, 0.12) 100%)',
              color: '#0038A7',
              border: '1px solid rgba(0, 56, 167, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Mail size={22} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0B1220', letterSpacing: '-0.3px' }}>
              Transmit Evaluation Feedback
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '0.82rem', color: 'var(--px-text-muted)' }}>
              Official tender evaluation observations for <strong>{proposal.vendor_name}</strong>
            </p>
          </div>
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={sending}
            style={{ minWidth: 140, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            {sending ? 'Transmitting...' : <><Mail size={15} /> Send Feedback</>}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {proposal.vendor_email ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 8,
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              color: '#1e3a8a',
              fontSize: '0.84rem'
            }}
          >
            <Mail size={16} style={{ color: '#0038A7', flexShrink: 0 }} />
            <span>
              Official notice will be delivered to: <strong>{proposal.vendor_email}</strong>
            </span>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 8,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              color: '#92400e',
              fontSize: '0.84rem'
            }}
          >
            <AlertTriangle size={16} style={{ color: '#d97706', flexShrink: 0 }} />
            <span>No vendor email on file — feedback will be archived in the system.</span>
          </div>
        )}

        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">
            Feedback Observations & Technical Query Notes <span className="required">*</span>
          </label>
          <textarea
            className="form-textarea"
            value={content}
            onChange={e => { setContent(e.target.value); setFieldError(''); }}
            style={{ minHeight: 180, fontSize: '0.86rem', lineHeight: 1.6 }}
            placeholder={`Dear ${proposal.vendor_name},\n\nThank you for your proposal submission. Following our comprehensive technical and commercial review, we request clarification on the following items:\n\n1. Technical Compliance: ...\n2. Commercial Pricing & Milestones: ...\n\nPlease furnish your revised response by [Date].\n\nSincerely,\nProcurement Evaluation Committee`}
          />
          {fieldError && (
            <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: 4 }}>
              {fieldError}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
